/**
 * @fileoverview SectionHeader — unified section header for all marketing pages.
 *
 * Replaces the duplicated badge + heading + description pattern that was
 * copy-pasted across every homepage section, features page, about page, etc.
 *
 * Supports:
 * - Eyebrow badge with configurable icon and color
 * - Two heading sizes (display / headline)
 * - Optional gradient-highlighted text span within heading
 * - Description paragraph
 * - Staggered fade-in animations with reduced-motion support
 */
import React from 'react';

type SectionHeaderProps = {
  /** Small uppercase text above the heading (e.g. "Core Features") */
  eyebrow?: string;
  /** Icon rendered inside the eyebrow badge */
  eyebrowIcon?: React.ReactNode;
  /** Main heading text */
  heading: React.ReactNode;
  /** Description paragraph below the heading */
  description?: React.ReactNode;
  /** Text alignment */
  align?: 'center' | 'left';
  /** Size variant — 'display' for hero-level, 'headline' for section-level */
  size?: 'display' | 'headline';
  /** HTML heading tag to render */
  as?: 'h1' | 'h2' | 'h3';
  /** Max width constraint for the header block */
  maxWidth?: string;
  /** Additional className for the container */
  className?: string;
};

export function SectionHeader({
  eyebrow,
  eyebrowIcon,
  heading,
  description,
  align = 'center',
  size = 'headline',
  as: Component = 'h2',
  maxWidth = 'max-w-3xl',
  className = '',
}: SectionHeaderProps) {
  const alignClass = align === 'center' ? 'text-center mx-auto' : 'text-left';

  const headingClass =
    size === 'display'
      ? 'text-display-xl text-on-surface font-bold tracking-tight'
      : 'text-headline-lg text-on-surface font-bold tracking-tight sm:text-4xl md:text-5xl';

  return (
    <div className={`${maxWidth} ${alignClass} ${className}`}>
      {/* Eyebrow badge */}
      {eyebrow && (
        <div
          className="animate-fade-in-up mb-6 inline-flex items-center gap-2.5 rounded-full border border-pb-slate-200/90 bg-pb-slate-100/80 px-4 py-1.5 text-[12px] font-bold tracking-[0.12em] uppercase text-pb-slate-700 shadow-sm backdrop-blur-sm dark:border-pb-white/[0.1] dark:bg-pb-white/[0.05] dark:text-pb-slate-200"
        >
          {eyebrowIcon && (
            <span className="text-pb-blue-600 dark:text-pb-blue-400" aria-hidden="true">
              {eyebrowIcon}
            </span>
          )}
          {eyebrow}
        </div>
      )}

      {/* Heading */}
      <Component
        className={`animate-fade-in-up ${headingClass}`}
        style={{ animationDelay: '80ms' }}
      >
        {heading}
      </Component>

      {/* Description */}
      {description && (
        <p
          className={`animate-fade-in-up text-base md:text-lg text-pb-slate-600 dark:text-pb-slate-300 mt-5 leading-relaxed ${align === 'center' ? 'max-w-2xl mx-auto' : 'max-w-2xl'}`}
          style={{ animationDelay: '160ms' }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/**
 * Highlighted text span for use inside headings.
 * Renders a gradient-colored text fragment.
 */
export function GradientText({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`text-pb-blue-600 dark:text-pb-blue-400 ${className}`}
    >
      {children}
    </span>
  );
}
